import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDataContext } from '../../../context/dataContainer';
import { ValidFieldTypes } from '../../../types';
import { ECardType } from '../../../utils/config';
import { validateCardNumber } from '../../../utils/validate';
import { MaskText } from '../../FormContainer/FormTypes/MaskText';
import { amexMask, identifyPotentialCardType, otherMask, otherMask_19 } from './utils';
import type { FieldComponentProps } from '../../../types';
import type { SyntheticEvent } from 'react';

export const CardNumber = ({ isFetching, handleCardTypeChange }: FieldComponentProps) => {
  const { watch, setValue } = useFormContext();
  const { predefinedCardType, paymentMethod } = useDataContext();
  const [potentialCardType, setPotentialCardType] = useState<ECardType | null>(predefinedCardType || null);

  const cardNumberName = ValidFieldTypes.CARD_NUMBER;
  const cardNumberWatch = watch(cardNumberName);

  const handleChange = useCallback((e: SyntheticEvent) => {
    const { value } = e.target as HTMLInputElement;
    if (value?.includes('_')) {
      setValue(cardNumberName, value.replaceAll('_', ''), { shouldValidate: true });
    } else {
      setValue(cardNumberName, value, { shouldValidate: true });
    }
  }, []);

  useEffect(() => {
    if (cardNumberWatch) {
      const type = identifyPotentialCardType(cardNumberWatch, predefinedCardType);
      setPotentialCardType(type);
    } else if (cardNumberWatch === '') {
      setPotentialCardType(null);
    }
  }, [cardNumberWatch]);

  useEffect(() => {
    handleCardTypeChange(potentialCardType);
  }, [potentialCardType]);

  return (
    <MaskText
      {...{
        type: 'text',
        className: classNames(potentialCardType?.toLowerCase(), {
          'credit-card': !!potentialCardType,
        }),
        registerOptions: {
          onChange: handleChange,
        },
        params: {
          name: cardNumberName,
          required: true,
          placeholder: 'xxxx xxxx xxxx xxxx',
          validate: (cardNumber) => validateCardNumber(cardNumber, paymentMethod?.credit || []),
          mask: (value: string) => {
            const digitsArr = value.match(/\d|x/g);
            return potentialCardType === ECardType.AMEX
              ? amexMask
              : potentialCardType !== ECardType.MASTERCARD && digitsArr && digitsArr?.length > 16
                ? otherMask_19
                : otherMask;
          },
          disabled: isFetching,
        },
      }}
    />
  );
};
